import { GetApiResponseByUrl } from '@crossbeam/openapi';

import { sortBy } from 'lodash';
import { computed, ref } from 'vue';

import { crossbeamApi } from '@/api';
import useHasOpenDataPartnerAccess from '@/composables/useHasOpenDataPartnerAccess';
import { captureException } from '@/errors';
import {
  useBillingStore,
  useDataSharesStore,
  usePartnersStore,
} from '@/stores';
import type { ManagedOfflinePartner } from '@/types/partners';

type ConvertedPartnerPair = GetApiResponseByUrl<
  '/v0.1/mop/partnerships/convert-to-odp',
  'post'
>['converted'][0];

function readConvertedFromError(error: unknown): ConvertedPartnerPair[] {
  const body = (error ?? {}) as { converted?: ConvertedPartnerPair[] };
  return Array.isArray(body.converted) ? body.converted : [];
}

const allManagedOfflinePartners = ref<ManagedOfflinePartner[]>([]);

// Upgrading never grants more lifetime additions, so a swap-exhausted block gets its own
// copy and a Talk to Sales action rather than the concurrent-quota upgrade prompt. Shared
// across every consumer so the message cannot drift between them.
export const OPEN_DATA_SWAPS_EXHAUSTED_MESSAGE =
  "You won't be able to switch this Open Data Partner";
export const OPEN_DATA_SWAPS_EXHAUSTED_DESCRIPTION =
  'Talk to our team about adding more Open Data Partners.';

function costsLifetimeAddition(partner: ManagedOfflinePartner): boolean {
  return partner.counts_against_additions_cap === true;
}

export default function useManagedOfflinePartner() {
  const partnersStore = usePartnersStore();
  const dataSharesStore = useDataSharesStore();
  const billingStore = useBillingStore();
  const hasOpenDataPartnerAccess = useHasOpenDataPartnerAccess();

  async function refreshManagedOfflinePartners() {
    const hasAddOpenDataPartner = hasOpenDataPartnerAccess.value;
    const { data, error } = await crossbeamApi.GET('/v0.1/mop/organizations', {
      params: {
        query: hasAddOpenDataPartner ? { sort: 'suggestion_score' } : {},
      },
    });
    if (error) {
      captureException(error);
      allManagedOfflinePartners.value = [];
      return;
    }
    allManagedOfflinePartners.value = data?.items
      ? hasAddOpenDataPartner
        ? data.items
        : sortBy(data.items, 'name')
      : [];
  }

  const availableManagedOfflinePartners = computed(() => {
    const existingPartnerIds = new Set(
      partnersStore.managedOfflinePartners.map((partner) => partner.id),
    );

    return allManagedOfflinePartners.value.filter(
      (partner) => !existingPartnerIds.has(partner.id),
    );
  });

  async function createManagedOfflinePartnership(
    partner: ManagedOfflinePartner,
  ): Promise<boolean> {
    const { error } = await crossbeamApi.POST('/v0.1/mop/partnerships', {
      body: { partner_organization_id: partner.id },
    });
    if (error) {
      captureException(error);
      return false;
    }
    return true;
  }

  async function refreshAfterPartnershipChanges() {
    await Promise.all([
      partnersStore.refreshPartnersStore(),
      dataSharesStore.refreshDataSharesStore(),
      billingStore.refreshBillingStore(),
    ]);
  }

  async function addManagedOfflinePartnership(
    partner: ManagedOfflinePartner,
  ): Promise<boolean> {
    const isSuccess = await createManagedOfflinePartnership(partner);
    if (!isSuccess) return false;
    await refreshAfterPartnershipChanges();
    return true;
  }

  type BulkCreateResult = {
    successPartners: ManagedOfflinePartner[];
    failureCount: number;
    refreshPromise: Promise<void> | null;
  };

  async function bulkCreateManagedOfflinePartnerships(
    partners: ManagedOfflinePartner[],
  ): Promise<BulkCreateResult> {
    const results = await Promise.all(
      partners.map(createManagedOfflinePartnership),
    );
    const successPartners = partners.filter((_, i) => results[i]);
    const failureCount = partners.length - successPartners.length;
    let refreshPromise: Promise<void> | null = null;
    if (successPartners.length) {
      refreshPromise = refreshAfterPartnershipChanges().catch((err) => {
        captureException(err);
        return Promise.resolve();
      });
    }
    return { successPartners, failureCount, refreshPromise };
  }

  type ConvertToOpenDataResult = {
    convertedPairs: ConvertedPartnerPair[];
    failureCount: number;
    /**
     * Null when nothing converted. Deliberately not started here: the caller
     * decides when the partner list may change under it.
     */
    refreshStores: (() => Promise<void>) | null;
  };

  async function convertManagedOfflinePartnersToOpenData(
    mopOrganizationIds: number[],
  ): Promise<ConvertToOpenDataResult> {
    if (!mopOrganizationIds.length)
      return { convertedPairs: [], failureCount: 0, refreshStores: null };

    const { data, error } = await crossbeamApi.POST(
      '/v0.1/mop/partnerships/convert-to-odp',
      { body: { mop_organization_ids: mopOrganizationIds } },
    );
    if (error) captureException(error);

    // Validation failures (400 / 404) convert nothing, but a mid-batch failure
    // answers 500 with the conversions that did complete - those partnerships
    // are already gone, so they still count as converted and still need a
    // store refresh.
    const convertedPairs = data?.converted ?? readConvertedFromError(error);
    return {
      convertedPairs,
      failureCount: mopOrganizationIds.length - convertedPairs.length,
      refreshStores: convertedPairs.length
        ? () =>
            refreshAfterPartnershipChanges().catch((err) => {
              captureException(err);
              return Promise.resolve();
            })
        : null,
    };
  }

  const remainingLifetimeAdditions = computed(
    () => billingStore.remainingLifetimeAdditions,
  );

  // Free re-adds do not count against the cap, so callers pass only the cap-consuming count.
  // A block fires solely on an actual overage, which keeps re-adds allowed even at zero
  // remaining. (Replace flows have no exemption and compute their own exhaustion instead.)
  function hasExceededLifetimeCap(capConsumingCount: number): boolean {
    const remaining = remainingLifetimeAdditions.value;
    return remaining !== null && capConsumingCount > remaining;
  }

  return {
    addManagedOfflinePartnership,
    allManagedOfflinePartners,
    availableManagedOfflinePartners,
    bulkCreateManagedOfflinePartnerships,
    convertManagedOfflinePartnersToOpenData,
    costsLifetimeAddition,
    createManagedOfflinePartnership,
    hasExceededLifetimeCap,
    refreshAfterPartnershipChanges,
    refreshManagedOfflinePartners,
    remainingLifetimeAdditions,
  };
}
